import { authedGet, authedWrite } from './apiClient';

export function bonusTrackerKey(year: number): string {
  return `company-bonus:${year}:v2`;
}

export interface BonusEarned {
  closing: number;
  nps: number;
  members: number;
  hours: number;
  monday: number;
  hiring: number;
  discretionary: number;
  best: number;
  gs: number;
  total: number;
  closingTier: '17' | '20' | '25' | null;
  npsTier: '75' | '80' | null;
  membersHit: boolean;
  hoursHit: boolean;
  mondayHit: boolean;
  hiringHit: boolean;
  gsHit: boolean;
}

export interface BonusMonth {
  id?: number;
  locationCode?: string;
  year?: number;
  month: number;
  closingPct: number | null;
  npsPct: number | null;
  membersLast: number | null;
  membersCurrent: number | null;
  hoursLast: number | null;
  hoursCurrent: number | null;
  mondayDone: boolean;
  hiringOk: boolean;
  discretionary: number | null;
  bestScore: number | null;
  bestPaid: number | null;
  actualPaid: number | null;
  membersOverride: boolean;
  hoursOverride: boolean;
  gsSalesPct: number | null;
  earned?: BonusEarned;
}

/** A tracker month's link to payroll (owner, 2026-10-06): the pay period
 * holding the month's last day, and the live Manager Entry line if sent. */
export interface BonusPayrollMonth {
  month: number;
  reason: string;
  bonusPayroll: { payPeriodId: number; adpWeek: number; closed: boolean } | null;
  sent: { lineId: number; amount: number; payPeriodId: number } | null;
}

export interface BonusPlan {
  locationCode: string;
  year: number;
  managerName: string | null;
  hoursMin: number;
  months: BonusMonth[];
  manager?: { employeeId: number; name: string } | null;
  managerProblem?: string | null;
  payroll?: BonusPayrollMonth[];
}

/** Where a month stands against Manager Entry. Pure. */
export type BonusSendState = 'none' | 'ready' | 'onPayroll' | 'changed' | 'remove';
export function bonusSendState(paid: number | null, link: BonusPayrollMonth | undefined): BonusSendState {
  const amount = paid ?? 0;
  const sent = link?.sent ?? null;
  if (!sent) return amount > 0 ? 'ready' : 'none';
  if (amount <= 0) return 'remove';
  return Math.abs(sent.amount - amount) < 0.005 ? 'onPayroll' : 'changed';
}

export interface BonusTrackerData {
  year: number;
  years: number[];
  canWrite: boolean;
  plans: BonusPlan[];
}

export async function fetchBonusTracker(year: number): Promise<BonusTrackerData> {
  return authedGet(`/api/v1/company/bonus-tracker?year=${year}`);
}

export async function saveBonusHoursMin(locationCode: string, year: number, hoursMin: number): Promise<void> {
  await authedWrite('PATCH', '/api/v1/company/bonus-tracker', {
    locationCode,
    year,
    hoursMin,
  });
}

export async function saveBonusMonth(month: BonusMonth, locationCode: string, year: number): Promise<void> {
  await authedWrite('PATCH', '/api/v1/company/bonus-tracker', {
    locationCode,
    year,
    month: month.month,
    closingPct: month.closingPct,
    npsPct: month.npsPct,
    membersLast: month.membersLast,
    membersCurrent: month.membersCurrent,
    hoursLast: month.hoursLast,
    hoursCurrent: month.hoursCurrent,
    mondayDone: month.mondayDone,
    hiringOk: month.hiringOk,
    discretionary: month.discretionary,
    bestScore: month.bestScore,
    bestPaid: month.bestPaid,
    actualPaid: month.actualPaid,
    membersOverride: month.membersOverride,
    hoursOverride: month.hoursOverride,
    gsSalesPct: month.gsSalesPct,
  });
}

function roundPct(value: number | null): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  const frac = value - Math.floor(value);
  return frac >= 0.5 ? Math.ceil(value) : Math.floor(value);
}

export function earnMonth(row: BonusMonth, hoursMin = 1700): BonusEarned {
  const close = roundPct(row.closingPct);
  const closing = close == null ? 0 : close >= 25 ? 450 : close >= 20 ? 250 : close >= 17 ? 125 : 0;
  const npsR = roundPct(row.npsPct);
  const nps = npsR == null ? 0 : npsR >= 80 ? 200 : npsR >= 75 ? 100 : 0;
  const members = row.membersOverride
    || (row.membersLast != null && row.membersCurrent != null && row.membersCurrent - row.membersLast >= 10)
    ? 100 : 0;
  const hours = row.hoursOverride
    || (row.hoursLast != null && row.hoursCurrent != null && row.hoursCurrent > row.hoursLast && row.hoursCurrent >= hoursMin)
    ? 100 : 0;
  const monday = row.mondayDone ? 50 : 0;
  const hiring = row.hiringOk ? 100 : 0;
  const discretionary = Math.max(0, row.discretionary ?? 0);
  const best = Math.max(0, row.bestPaid ?? 0);
  const gsR = roundPct(row.gsSalesPct);
  const gs = gsR == null ? 0 : gsR >= 100 ? 200 : gsR >= 80 ? 100 : 0;
  return {
    closing, nps, members, hours, monday, hiring, discretionary, best, gs,
    total: closing + nps + members + hours + monday + hiring + discretionary + best + gs,
    closingTier: closing === 450 ? '25' : closing === 250 ? '20' : closing === 125 ? '17' : null,
    npsTier: nps === 200 ? '80' : nps === 100 ? '75' : null,
    membersHit: members > 0,
    hoursHit: hours > 0,
    mondayHit: monday > 0,
    hiringHit: hiring > 0,
    gsHit: gs > 0,
  };
}

export function emptyMonth(month: number): BonusMonth {
  return {
    month,
    closingPct: null,
    npsPct: null,
    membersLast: null,
    membersCurrent: null,
    hoursLast: null,
    hoursCurrent: null,
    mondayDone: false,
    hiringOk: false,
    discretionary: null,
    bestScore: null,
    bestPaid: null,
    actualPaid: null,
    membersOverride: false,
    hoursOverride: false,
    gsSalesPct: null,
  };
}

export function money(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US')}`;
}
